import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAICategories,
  useCreateTrainingData,
  useDeleteTrainingData,
  useTrainingData,
  useUpdateTrainingData,
} from '@/hooks/useAITraining';
import { useOrganizations } from '@/hooks/useOrganizations';
import type {
  AITrainingData,
  CreateTrainingDataRequest,
  UpdateTrainingDataRequest,
} from '@/services/ai-training.service';
import { Brain, Check, Plus, Save, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { TrainingDataTable } from './TrainingDataTable';
import AgentCapabilitiesCard from './AgentCapabilitiesCard';
import {
  SettingsDialogLayout,
  SettingsFieldGrid,
  SettingsPanel,
  SettingsToolbar,
} from './settings/SettingsConsole';
import { useTranslation } from 'react-i18next';

const AITrainingManagement: React.FC = () => {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AITrainingData | null>(null);
  const [deleteItem, setDeleteItem] = useState<AITrainingData | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('true');
  const [organizationFilter, setOrganizationFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data: organizationsData } = useOrganizations();
  const organizations = organizationsData?.data || [];

  const { data: trainingResponse, isLoading } = useTrainingData({
    page,
    limit: 20,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    is_active: statusFilter === 'all' ? 'all' : statusFilter,
    organizationId: organizationFilter || undefined,
  });

  const createMutation = useCreateTrainingData();
  const updateMutation = useUpdateTrainingData();
  const deleteMutation = useDeleteTrainingData();
  const categories = useAICategories();

  const entries = trainingResponse?.data.training_data ?? [];
  const pagination = trainingResponse?.data.pagination;
  const totalItems = pagination?.total ?? entries.length;

  const [formData, setFormData] = useState<CreateTrainingDataRequest>({
    title: '',
    description: '',
    training_prompt: '',
    category: 'general',
    example_input: '',
    example_output: '',
    priority: 0,
    organizationId: '',
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      training_prompt: '',
      category: 'general',
      example_input: '',
      example_output: '',
      priority: 0,
      organizationId: '',
    });
    setEditingItem(null);
  };

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(formData);
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success(t('common:x4.trainingDataCreated', 'Trainingsdaten erstellt'));
    } catch (error) {
      console.error('Error creating training data:', error);
      toast.error(t('common:x4.createFailed', 'Fehler beim Erstellen'));
    }
  };

  const handleEdit = (item: AITrainingData) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      training_prompt: item.training_prompt,
      category: item.category,
      example_input: item.example_input || '',
      example_output: item.example_output || '',
      priority: item.priority,
    });
    setIsCreateDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    try {
      await updateMutation.mutateAsync({
        id: editingItem.id,
        data: formData as UpdateTrainingDataRequest,
      });
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success(t('common:x4.trainingDataUpdated', 'Trainingsdaten aktualisiert'));
    } catch (error) {
      console.error('Error updating training data:', error);
      toast.error(t('common:x4.updateFailed2', 'Fehler beim Aktualisieren'));
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await deleteMutation.mutateAsync(deleteItem.id);
      setDeleteItem(null);
      toast.success(t('common:x4.trainingDataDeleted', 'Trainingsdaten gelöscht'));
    } catch (error) {
      console.error('Error deleting training data:', error);
      toast.error(t('common:x4.deleteFailed2', 'Fehler beim Löschen'));
    }
  };

  const toggleStatus = async (item: AITrainingData) => {
    try {
      await updateMutation.mutateAsync({
        id: item.id,
        data: { is_active: !item.is_active },
      });
      toast.success(t('common:state.statusUpdated', 'Status aktualisiert'));
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(t('common:x1.statusChangeFailed', 'Status konnte nicht geändert werden'));
    }
  };

  const getCategoryLabel = (category: string) => {
    const cat = categories.find((c) => c.value === category);
    return cat?.label || category;
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    resetForm();
  };

  return (
    <Dialog
      open={isCreateDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCloseDialog();
        } else {
          setIsCreateDialogOpen(true);
        }
      }}
    >
      <div className="space-y-5">
        <AgentCapabilitiesCard />
        <SettingsPanel
          title={t('common:x4.trainingData', 'Trainingsdaten')}
          description="Gezielte Regeln, Beispiele und Prozesshinweise fuer konsistente KI-Antworten."
          action={
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setIsCreateDialogOpen(true);
                }}
                className="h-9 gap-2 rounded-lg bg-[#0f172a] px-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                {t('common:x4.newTrainingData', 'Neue Trainingsdaten')}
              </Button>
            </DialogTrigger>
          }
          bodyClassName="space-y-4"
        >
          <SettingsToolbar>
            <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
              <SlidersHorizontal className="h-4 w-4" />
              {t('common:x4.filter', 'Filter')}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <FilterField label="Organisation">
                <Select
                  value={organizationFilter || 'all'}
                  onValueChange={(value) => setOrganizationFilter(value === 'all' ? '' : value)}
                >
                  <SelectTrigger className="h-10 w-full rounded-lg border-slate-200 bg-white text-sm">
                    <SelectValue placeholder={t('common:state.allOrgs', 'Alle Organisationen')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common:state.allOrgs', 'Alle Organisationen')}</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>

              <FilterField label="Kategorie">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-10 w-full rounded-lg border-slate-200 bg-white text-sm">
                    <SelectValue placeholder={t('common:x1.allCategories', 'Alle Kategorien')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common:x1.allCategories', 'Alle Kategorien')}</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>

              <FilterField label="Status">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-10 w-full rounded-lg border-slate-200 bg-white text-sm">
                    <SelectValue placeholder={t('common:field.status', 'Status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common:state.allStatus', 'Alle Status')}</SelectItem>
                    <SelectItem value="true">{t('common:state.active', 'Aktiv')}</SelectItem>
                    <SelectItem value="false">{t('common:state.inactive', 'Inaktiv')}</SelectItem>
                  </SelectContent>
                </Select>
              </FilterField>
            </div>
          </SettingsToolbar>

          <TrainingDataTable
            entries={entries}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={(item) => setDeleteItem(item)}
            onToggleStatus={toggleStatus}
            isStatusPending={updateMutation.isPending}
            getCategoryLabel={getCategoryLabel}
          />

          {pagination && pagination.pages > 1 && (
            <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500">
                {t('common:field.page', 'Seite')} {pagination.page} von {pagination.pages} · {totalItems} Einträge
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="h-8 rounded-lg border-slate-200 text-xs"
                >
                  {t('common:action.back', 'Zurück')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.pages}
                  className="h-8 rounded-lg border-slate-200 text-xs"
                >
                  {t('common:action.next', 'Weiter')}
                </Button>
              </div>
            </div>
          )}
        </SettingsPanel>
      </div>

      <DialogContent className="flex max-h-[90vh] overflow-hidden rounded-2xl border-0 p-0 sm:max-w-[980px]">
        <SettingsDialogLayout
          icon={Brain}
          asideTitle={editingItem ? 'Training bearbeiten' : 'Neues Training'}
          asideDescription="Praezise Regeln funktionieren am besten mit realen Kundennachrichten und einer klar erwarteten Antwort."
          title={t('common:x4.configureTrainingData', 'Trainingsdaten konfigurieren')}
          description="Organisation, Kategorie, Prompt und optionale Beispiele festlegen."
          asideContent={
            <div className="rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-4">
              <span className="mb-3 block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                {t('common:x4.checklist', 'Checkliste')}
              </span>
              <ul className="space-y-2">
                {[
                  'Konkrete Situation beschreiben',
                  'Tonfall und Grenzen nennen',
                  'Optional Input/Output ergänzen',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-slate-600">
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          }
          footer={
            <>
              <Button
                variant="outline"
                onClick={handleCloseDialog}
                className="h-9 rounded-lg border-slate-200 text-slate-600 hover:bg-white hover:text-slate-900"
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={editingItem ? handleUpdate : handleCreate}
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending ||
                  !formData.title ||
                  !formData.training_prompt
                }
                className="h-9 rounded-lg bg-[#0f172a] text-white hover:bg-slate-800 disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />
                {editingItem ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </>
          }
        >
          <TrainingDataForm
            formData={formData}
            setFormData={setFormData}
            categories={categories}
            organizations={organizations}
            isEdit={!!editingItem}
          />
        </SettingsDialogLayout>
      </DialogContent>

      <AlertDialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <AlertDialogContent className="max-w-md rounded-2xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-slate-950">
              {t('common:x4.deleteTrainingDataQ', 'Trainingsdaten löschen?')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              {t('common:x4.wantYouQ', 'Möchten Sie "')}{deleteItem?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-9 rounded-lg border-slate-200 px-3 text-sm">
              {t('common:action.cancel', 'Abbrechen')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="h-9 rounded-lg bg-red-600 px-3 text-sm text-white hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {t('common:action.delete', 'Löschen')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField: React.FC<FilterFieldProps> = ({ label, children }) => (
  <div className="min-w-0 space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
  </div>
);

interface TrainingDataFormProps {
  formData: CreateTrainingDataRequest;
  setFormData: (data: CreateTrainingDataRequest) => void;
  categories: Array<{ value: string; label: string }>;
  organizations: Array<{ id: string; name: string }>;
  isEdit?: boolean;
}

const TrainingDataForm: React.FC<TrainingDataFormProps> = ({
  formData,
  setFormData,
  categories,
  organizations,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      {!isEdit && (
        <FilterField label="Organisation *">
          <Select
            value={formData.organizationId || ''}
            onValueChange={(value) => setFormData({ ...formData, organizationId: value })}
          >
            <SelectTrigger className="h-10 rounded-lg border-slate-200">
              <SelectValue placeholder={t('ticket:ph.selectOrg', 'Organisation wählen')} />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}

      <SettingsFieldGrid>
        <FilterField label="Titel *">
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={t('common:x4.trainingDataTitle', 'Titel der Trainingsdaten')}
            className="h-10 rounded-lg border-slate-200 focus-visible:ring-[#0F99FD]/20"
          />
        </FilterField>
        <FilterField label="Kategorie">
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger className="h-10 rounded-lg border-slate-200">
              <SelectValue placeholder={t('common:x4.chooseCategory', 'Kategorie wählen')} />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      </SettingsFieldGrid>

      <FilterField label="Beschreibung">
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={t('common:x4.purposeOfTrainingData', 'Zweck dieser Trainingsdaten...')}
          className="h-10 rounded-lg border-slate-200 focus-visible:ring-[#0F99FD]/20"
        />
      </FilterField>

      <FilterField label="Trainingsanweisung *">
        <Textarea
          id="training_prompt"
          value={formData.training_prompt}
          onChange={(e) => setFormData({ ...formData, training_prompt: e.target.value })}
          placeholder={t('common:x4.trainingInstructionForAi', 'Trainingsanweisung für die KI...')}
          rows={6}
          className="min-h-[150px] resize-y rounded-lg border-slate-200 text-sm leading-6 focus-visible:ring-[#0F99FD]/20"
        />
      </FilterField>

      <SettingsFieldGrid>
        <FilterField label="Beispiel Eingabe">
          <Textarea
            id="example_input"
            value={formData.example_input}
            onChange={(e) => setFormData({ ...formData, example_input: e.target.value })}
            placeholder={t('common:x4.exampleCustomerMsg', 'Beispiel Kundennachricht...')}
            rows={3}
            className="min-h-[96px] resize-y rounded-lg border-slate-200 text-sm leading-6 focus-visible:ring-[#0F99FD]/20"
          />
        </FilterField>
        <FilterField label="Beispiel Ausgabe">
          <Textarea
            id="example_output"
            value={formData.example_output}
            onChange={(e) => setFormData({ ...formData, example_output: e.target.value })}
            placeholder={t('common:x4.expectedAiReply', 'Erwartete KI-Antwort...')}
            rows={3}
            className="min-h-[96px] resize-y rounded-lg border-slate-200 text-sm leading-6 focus-visible:ring-[#0F99FD]/20"
          />
        </FilterField>
      </SettingsFieldGrid>

      <FilterField label="Priorität (0-10)">
        <Input
          id="priority"
          type="number"
          min="0"
          max="10"
          value={formData.priority}
          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
          className="h-10 w-28 rounded-lg border-slate-200 focus-visible:ring-[#0F99FD]/20"
        />
      </FilterField>
    </div>
  );
};

export default AITrainingManagement;
